import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatKarachiDateTime } from '@/lib/dates'
import { openWorkspaceDb } from '@/offline/db'
import { pushOutbox } from '@/offline/sync'
import { supabaseTransport } from '@/offline/transport'
import { pullChanges } from '@/offline/pull'
import { listConflicts, resolveKeepMine, resolveUseServer } from '@/offline/conflicts'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'

/** Sync centre: push pending ops, pull server changes, resolve conflicts explicitly. */
export function SyncPage() {
  const ws = getActiveWorkspaceId()
  const pending = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).outbox.count() : 0), [ws])
  const conflicts = useLiveQuery(async () => (ws ? listConflicts(ws) : []), [ws])
  const cursor = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).pull_cursor.get(ws) : undefined), [ws])

  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function syncNow() {
    if (!ws) return
    setBusy(true)
    setError(null)
    try {
      const pushed = await pushOutbox(openWorkspaceDb(ws), supabaseTransport())
      const pulled = await pullChanges(ws)
      setMsg(
        `Synced: ${pushed.applied + pushed.replayed} pushed, ${pushed.conflicts.length} conflict(s), ${pulled.pulled} pulled.`,
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function keepMine(opId: string) {
    if (!ws) return
    const op = (conflicts ?? []).find((c) => c.operation_id === opId)
    if (!op) return
    setBusy(true)
    setError(null)
    try {
      const summary = await resolveKeepMine(ws, op)
      setMsg(
        summary.conflicts.length > 0
          ? 'Still conflicting — the server changed again. Retry or use server version.'
          : 'Your version pushed.',
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function useServer(opId: string) {
    if (!ws) return
    const op = (conflicts ?? []).find((c) => c.operation_id === opId)
    if (!op) return
    setBusy(true)
    setError(null)
    try {
      await resolveUseServer(ws, op)
      setMsg('Local draft discarded; server version pulled.')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page title="Sync & conflicts" note="Offline-first: push local changes, pull server changes.">
      <div className="mb-3 grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2.5">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Pending changes</p>
          <p className={`mt-0.5 text-lg font-semibold tabular-nums ${pending ? 'text-amber-400' : 'text-emerald-400'}`}>
            {pending ?? 0}
          </p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2.5">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Server cursor</p>
          <p className="mt-0.5 text-lg font-semibold tabular-nums text-slate-100">
            {cursor?.last_server_sequence ?? 0}
          </p>
          {cursor?.updated_at ? <p className="text-[10px] text-slate-500">{formatKarachiDateTime(cursor.updated_at)}</p> : null}
        </div>
      </div>

      <button
        type="button"
        onClick={() => void syncNow()}
        disabled={busy}
        className="w-full rounded-xl bg-sky-500 px-4 py-3 text-base font-semibold text-white disabled:opacity-60"
      >
        {busy ? 'Syncing…' : 'Sync now'}
      </button>

      {msg ? <p className="mt-2 text-center text-xs text-emerald-400">{msg}</p> : null}
      {error ? <p role="alert" className="mt-2 text-center text-xs text-rose-300">{error}</p> : null}

      <h2 className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Conflicts ({(conflicts ?? []).length})
      </h2>
      {(conflicts ?? []).length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-700 px-4 py-6 text-center text-sm text-slate-500">
          No conflicts. Both drafts are only kept when the server reports a same-revision edit.
        </p>
      ) : (
        <ul className="space-y-2">
          {(conflicts ?? []).map((op) => (
            <li key={op.operation_id} className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
              <p className="text-sm font-medium text-amber-200">
                {op.entity_type} • {op.action}
              </p>
              <p className="text-[11px] text-slate-400">queued {formatKarachiDateTime(op.created_at)}</p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => void keepMine(op.operation_id)}
                  disabled={busy}
                  className="flex-1 rounded-lg bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                >
                  Keep mine
                </button>
                <button
                  type="button"
                  onClick={() => void useServer(op.operation_id)}
                  disabled={busy}
                  className="flex-1 rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-200 disabled:opacity-60"
                >
                  Use server
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Page>
  )
}
